/**
 * BOARD-AS-SPEC-1 (s222, D-221.40) — the interview board's screen data, typed, and the small readers the
 * screens call.
 *
 * `BOARD_STEPS` is GENERATED from the committed board (`npm run board:generate`) and checked against it on
 * every push (`npm run check:board-parity`). A screen reads its look, copy, columns, cell kinds, options and
 * notes from here instead of re-typing them.
 *
 * 🔴 The limit (STANDING-RULES § How screens get built): a board carries look and words. It does NOT carry
 * where an answer is stored, who may read it, or what executor task it creates — those stay in written code.
 * Board wording → stored value, and board cell key → product field key, go through `board-map.ts`.
 */

import { BOARD_STEPS } from "./board-steps.generated";
import { FROM_KID, OPTION_VALUE } from "./board-map";
import { ageAtDeath } from "../age";

// s231 (D-231.80): `rowOnly` — a tile the board draws only as a row kind (exec's Backup), never offered as a pick.
export type BoardTile = { id: string; label: string; sub?: string; mark?: string; color?: string; full?: string; typed?: boolean; rowOnly?: boolean };
export type BoardNone = { label: string; sub?: string; prune: number; delta?: string };
export type BoardGroup = { cols: number; tiles: BoardTile[]; none?: BoardNone };
export type BoardHeader = { label: string; sub?: string; strong?: boolean };
export type BoardCellKind = "joint" | "date" | "picker" | "text" | "seg";
export type BoardCell = {
  kind: BoardCellKind;
  k: string;
  title?: string;
  word?: string;
  undo?: string;
  ph?: string;
  opts?: string[];
  org?: boolean;
  /** s236 (D-236.29(a)): the company card by tile id — the charity tile on benef. */
  orgBy?: Record<string, boolean>;
  /** s236 (D-236.87): follow-up questions on the row's second line while this (date) cell is empty — Children's Under 18?. */
  emptyThen?: Array<{ kind: BoardCellKind; k: string; label?: string; ph?: string; opts?: string[]; hint?: string }>;
  long?: boolean;
  bigTitle?: string;
  bigHint?: string;
  bigPh?: string;
  sample?: string;
  fromPlan?: boolean;
  fromKid?: string;
  onWhen?: { k: string; vIn: string[] };
};
export type BoardTable = { cols: string; headers: BoardHeader[]; cells: BoardCell[]; rowsLead?: string; rowsNote?: string };
export type BoardStep = {
  key: string;
  crumb: string;
  title: string;
  why?: string;
  groups?: BoardGroup[];
  autoPick?: boolean;
  noDup?: string[];
  rowsLead?: string;
  rowsNote?: string;
  cols?: string;
  headers?: BoardHeader[];
  cells?: BoardCell[];
  second?: Partial<BoardTable> & { tiles: string[] };
  pickNotes?: Record<string, Record<string, string>>;
  noteFor?: Record<string, string>;
  rules?: Record<string, Record<string, boolean>>;
  nextNote?: string;
  /** s226 (Reade, option b): a named row with no standing asks for it on that row only; `ph` is the picker's empty word.
   *  `canvasLink` is the canvas's own demo link and is never built. */
  noStanding?: { ph: string; canvasLink?: string };
  /** s233 (D-232.16): a backup row's own told choice — the picker's empty word (`ph`, after the row's label), its two
   *  choices (each drawn after the row's label: "Backup 1, and they know"), and the note under a not-told backup. */
  backupStanding?: { ph: string; opts: string[]; untoldNote: string };
  /** s233 (D-233.14): a NAMED executor's standing is a dropdown of the same two choices (the tiles stay for adding). */
  execStanding?: { ph: string; opts: string[] };
};

export const BOARD: readonly BoardStep[] = BOARD_STEPS as readonly BoardStep[];

/** A screen's board declaration. Throws on an unknown key — a screen reading a step the board lacks is a bug. */
export function boardStep(key: string): BoardStep {
  const s = BOARD.find((x) => x.key === key);
  if (!s) throw new Error(`board: no screen "${key}"`);
  return s;
}

/** A cell by its board key, in the first table or the second. Throws if absent. */
export function boardCell(step: BoardStep, k: string): BoardCell {
  const c = [...(step.cells ?? []), ...(step.second?.cells ?? [])].find((x) => x.k === k);
  if (!c) throw new Error(`board: screen "${step.key}" has no cell "${k}"`);
  return c;
}

/**
 * A table as the row-table components take it. The board's headers name the lead column and then one column
 * per cell; a `joint` cell (the contact control) sits under the header after the lead. Column keys are React
 * keys only (never shown), taken from the lead and the cells' `k`.
 */
// s231 (D-231.80): `omit` — cell keys the board draws ahead of the product (board-map.ts DRAWN_CELLS). Their column
// and its grid track (same position) are left out, so a built screen shows only what it builds.
export function boardTable(t: { cols?: string; headers?: BoardHeader[]; cells?: BoardCell[]; rowsLead?: string; rowsNote?: string }, omit: readonly string[] = []) {
  const keys = ["lead", ...(t.cells ?? []).map((c) => c.k)];
  const all = (t.headers ?? []).map((h, i) => ({ key: keys[i] ?? `col${i}`, label: h.label, ...(h.sub ? { sub: h.sub } : {}), ...(h.strong ? { strong: true } : {}) }));
  const dropped = new Set(all.map((c, i) => (omit.includes(c.key) ? i : -1)).filter((i) => i >= 0));
  const tracks = gridTracks(t.cols ?? "");
  return {
    columns: all.filter((_, i) => !dropped.has(i)),
    gridTemplate: dropped.size ? tracks.filter((_, i) => !dropped.has(i)).join(" ") : (t.cols ?? ""),
    rowsLead: t.rowsLead || undefined,
    rowsNote: t.rowsNote || undefined,
  };
}

/** A CSS grid template's top-level tracks ("210px minmax(190px, 1fr) 216px" → three). Pure. */
export function gridTracks(cols: string): string[] {
  const out: string[] = [];
  let depth = 0, cur = "";
  for (const ch of cols.trim()) {
    if (ch === "(") depth++;
    if (ch === ")") depth--;
    if (/\s/.test(ch) && depth === 0) { if (cur) out.push(cur); cur = ""; continue; }
    cur += ch;
  }
  if (cur) out.push(cur);
  return out;
}

/** A picker's or segment's options: the board's words as labels, the map's stored values as values. */
export function boardOptions(screen: string, cell: BoardCell): { value: string; label: string }[] {
  const map = OPTION_VALUE[screen]?.[cell.k];
  return (cell.opts ?? []).map((label) => {
    const value = map ? map[label] : label;
    if (value === undefined) throw new Error(`board: "${screen}.${cell.k}" option "${label}" has no stored value in board-map.ts`);
    return { value, label };
  });
}

/** A cell's pick notes re-keyed by STORED value (the screens look notes up by what is stored). */
export function boardPickNotes(screen: string, k: string): Record<string, string> {
  const step = boardStep(screen);
  const notes = step.pickNotes?.[k] ?? {};
  const map = OPTION_VALUE[screen]?.[k];
  const out: Record<string, string> = {};
  for (const [word, note] of Object.entries(notes)) out[map ? (map[word] ?? word) : word] = note;
  return out;
}

/** The screen's tile group (the first — chapter one's screens carry one) and its "none" answer. */
export function boardTiles(step: BoardStep): { tiles: BoardTile[]; none?: BoardNone; cols: number } {
  const g = step.groups?.[0];
  return { tiles: g?.tiles ?? [], none: g?.none, cols: g?.cols ?? 3 };
}

/** The board's word for a STORED value of a cell's options (the reverse of `boardOptions`), or undefined. Pure. */
export function boardWord(screen: string, k: string, stored: string | undefined): string | undefined {
  if (stored === undefined || stored === "") return undefined;
  const map = OPTION_VALUE[screen]?.[k];
  if (!map) return stored;
  return Object.entries(map).find(([, v]) => v === stored)?.[0];
}

/**
 * s223 — `onWhen` / `vIn` (D-222.10 part 4): a cell shows ONLY once another answer on the same row is one of the
 * named board words (the board: "the reverse of offWhen; the cell shows ONLY once the named answer is given").
 * `storedOf` reads the row's STORED value for a board cell key; it is compared in the board's words, so the rule
 * reads the way the board declares it. A cell with no `onWhen` is always open. Pure; shared by every screen.
 */
export function cellOpen(screen: string, cell: Pick<BoardCell, "onWhen">, storedOf: (k: string) => string | undefined): boolean {
  if (!cell.onWhen) return true;
  const word = boardWord(screen, cell.onWhen.k, storedOf(cell.onWhen.k));
  return word !== undefined && cell.onWhen.vIn.includes(word);
}

/** One of the planner's own children, as chapter one holds them. */
export type KidFacts = { rowId: string; firstName: string; surname: string; dob: string };

/**
 * s223 — `fromKid` (D-222.10 part 4): the value a cell carries in when the row's person is the planner's own child
 * from chapter one — `{ value }` to store and `{ word }` to show read-only — or null when there is nothing to carry
 * (the cell is then asked like any other). `today` is ISO `YYYY-MM-DD`. Pure; the declaration is `FROM_KID`.
 */
export function carryFromKid(screen: string, cell: Pick<BoardCell, "k" | "fromKid">, kid: Pick<KidFacts, "dob">, today: string): { value: string; word: string } | null {
  if (cell.fromKid === undefined) return null;
  const carry = FROM_KID[screen]?.[cell.k];
  if (!carry) throw new Error(`board: "${screen}.${cell.k}" carries fromKid but FROM_KID in board-map.ts does not say what`);
  if (carry.kind === "fixed") return { value: carry.value, word: cell.fromKid };
  if (carry.kind === "dob") return kid.dob ? { value: kid.dob, word: kid.dob } : null;
  const age = ageAtDeath(kid.dob, today);
  if (age === null) return null;
  const value = age < 18 ? "true" : "false";
  return { value, word: boardWord(screen, cell.k, value) ?? value };
}

/**
 * s223 — `rules` (D-223.18): a cell a TILE does not ask. The board declares it per tile
 * (`rules: { charity: { minor: false, dob: false } }`), and rule 45 says what the row then draws: "not asked
 * here" in light grey — never hidden, never an empty box. Pure; the render reads this, never a tile comparison
 * written into a screen.
 *
 * 🔴 It is a READ rule as much as a render one: a value the row no longer asks for counts as absent everywhere
 * (D-223.18 part 3, `shared/lib/row-gates` on the server side), and is never deleted (part 2).
 */
export function cellRuledOff(step: BoardStep, tileId: string | null | undefined, k: string): boolean {
  if (!tileId) return false;
  return step.rules?.[tileId]?.[k] === false;
}
